declare global {
  interface Window {
    recaptchaSuccessCallback: (token: string) => void
    recaptchaErrorCallback: (error?: Error) => void
    recaptchaExpiredCallback: () => void
    grecaptcha: { enterprise?: {} }
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const { grecaptcha } = useRuntimeConfig().public

  nuxtApp.provide('recaptcha', new ReCaptcha(grecaptcha))
})

const API_URL = 'https://www.recaptcha.net/recaptcha/api.js'

// 脚本加载超时：被同意管理平台扣住的脚本不会触发 load/error，需要一个上限把静默失败显式化
const RECAPTCHA_LOAD_TIMEOUT = 15000

// https://github.com/PierfrancescoSoffritti/light-event-bus.js/blob/master/src/EventBus.js
class EventBus {
  subscriptions: { [index: string]: { [index: symbol]: (arg: any) => void } } = {}
  
  subscribe(eventType: string, callback: () => void) {
    const subscriptions = this.subscriptions
    const id = Symbol('id')
    if (!subscriptions[eventType]) subscriptions[eventType] = {}
    subscriptions[eventType][id] = callback
    return {
      unsubscribe: function unsubscribe() {
        delete subscriptions[eventType][id]
        if (Object.getOwnPropertySymbols(subscriptions[eventType]).length === 0) {
          delete subscriptions[eventType]
        }
      },
    }
  }

  publish(eventType: string, arg: any) {
    const subscriptions = this.subscriptions
    if (!subscriptions[eventType]) return

    Object.getOwnPropertySymbols(subscriptions[eventType]).forEach((key) => subscriptions[eventType][key](arg))
  }
}
class ReCaptcha {
  _elements: any
  _grecaptcha: any
  _eventBus: any
  _ready: null | boolean | Promise<unknown>
  // 标记是否已经历过一次 init，用于区分首次加载与失败后的重试
  _retried: boolean

  hideBadge: boolean
  language: string | undefined
  siteKey: string
  version: number
  size: string
  mode: string

  constructor({
    hideBadge = false,
    language,
    mode = 'baes',
    siteKey,
    version = 2,
    size = 'normal',
  }: {
    hideBadge?: boolean
    language?: string
    siteKey?: string
    version?: number
    size?: string
    mode?: string
  }) {
    if (!siteKey) {
      throw new Error('ReCaptcha error: No key provided')
    }

    if (!version) {
      throw new Error('ReCaptcha error: No version provided')
    }

    this._elements = {}
    this._grecaptcha = null

    this._eventBus = null
    this._ready = false
    this._retried = false

    this.hideBadge = hideBadge
    this.language = language

    this.siteKey = siteKey
    this.version = version
    this.size = size

    this.mode = mode
  }

  destroy() {
    if (this._ready) {
      this._ready = false

      const { head } = document
      const { style } = this._elements

      const scripts = [...document.head.querySelectorAll('script')].filter((script) => script.src.includes('recaptcha'))

      if (scripts.length) {
        scripts.forEach((script) => head.removeChild(script))
      }

      if (head.contains(style)) {
        head.removeChild(style)
      }

      const badge = document.querySelector('.grecaptcha-badge')
      if (badge) {
        badge.remove()
      }
    }
  }

  async execute(action: any) {
    try {
      await this.init()

      if ('grecaptcha' in window) {
        return this._grecaptcha.execute(this.siteKey, { action })
      }
    } catch (error) {
      throw new Error(`ReCaptcha error: Failed to execute ${error}`)
    }
  }

  getResponse(widgetId: number) {
    return new Promise((resolve, reject) => {
      if ('grecaptcha' in window) {
        if (this.size == 'invisible') {
          this._grecaptcha.execute(widgetId)
          window.recaptchaSuccessCallback = (token) => {
            this._eventBus.publish('recaptcha-success', token)
            resolve(token)
          }
          window.recaptchaErrorCallback = (error) => {
            this._eventBus.publish('recaptcha-error', error)
            reject(error)
          }
        } else {
          const response = this._grecaptcha.getResponse(widgetId)

          if (response) {
            this._eventBus.publish('recaptcha-success', response)
            resolve(response)
          } else {
            const errorMessage = 'Failed to execute'

            this._eventBus.publish('recaptcha-error', errorMessage)
            reject(errorMessage)
          }
        }
      }
    })
  }

  /**
   * 返回类型固定为 Promise，调用方可以无条件 .catch()/await。
   * 内部 _ready 仍是 `null | boolean | Promise<unknown>`（首次同步为 false、加载中为 promise），
   * 这里统一收敛成 promise，避免调用侧出现 null/boolean 的类型分支。
   */
  init(): Promise<unknown> {
    if (this._ready) {
      // make sure caller waits until recaptcha get ready
      return Promise.resolve(this._ready)
    }

    // 只在重试路径清理残留：首次 init 时清掉会误删 recaptcha/api.js 自身注入的 gstatic 脚本
    if (this._retried) {
      document.head
        .querySelectorAll('script')
        .forEach((el) => {
          if (el.src && el.src.includes('recaptcha')) el.remove()
        })
    }
    this._retried = true

    // 事件总线只在首次 init 时创建。重试会把下面 this._elements 换成新的 script 元素，
    // 但组件的 on('recaptcha-success') 订阅是在 mounted 里一次性注册的，
    // 重建 bus 会让订阅挂在旧实例上，重试成功后 success 事件永远收不到。
    if (!this._eventBus) this._eventBus = new (EventBus as any)()

    this._elements = {
      script: document.createElement('script'),
      style: document.createElement('style'),
    }

    const { script, style } = this._elements

    script.setAttribute('async', '')
    script.setAttribute('defer', '')

    const params = []
    if (this.version === 3) {
      params.push('render=' + this.siteKey)
    }
    if (this.language) {
      params.push('hl=' + this.language)
    }

    let scriptUrl = API_URL

    if (this.mode === 'enterprise') {
      scriptUrl = scriptUrl.replace('api.js', 'enterprise.js')
      params.push('render=' + this.siteKey)
    }

    script.setAttribute('src', scriptUrl + '?' + params.join('&'))

    // Cookiebot 以 data-blockingmode="auto" 加载时会主动把未授权的第三方脚本扣成 text/plain，
    // 等拿到同意才放行。它的 consent 因方案到期/域名未授权而永远拿不到时，这个脚本就永远不会执行，
    // 页面表现为 KYC 内容区只剩一个空盒子。
    // reCAPTCHA 是功能性必要脚本，不涉及用户追踪，显式豁免同意流程。
    script.setAttribute('data-cookieconsent', 'ignore')

    window.recaptchaSuccessCallback = (token: string) => this._eventBus.publish('recaptcha-success', token)
    window.recaptchaExpiredCallback = () => this._eventBus.publish('recaptcha-expired')
    window.recaptchaErrorCallback = () => this._eventBus.publish('recaptcha-error', 'Failed to execute')

    const ready = new Promise((resolve, reject) => {
      // 脚本被 CMP/扩展/网络中间层扣住时，load 和 error 都不会触发，promise 永远不 settle，
      // 调用方 await 悬挂、UI 停在空盒子且没有任何反馈。这里用超时把静默失败转成显式失败。
      let settled = false
      const timer = setTimeout(() => {
        if (settled) return
        settled = true
        this._ready = null
        reject(new Error('ReCaptcha error: Timed out loading script'))
      }, RECAPTCHA_LOAD_TIMEOUT)

      script.addEventListener('load', () => {
        if (settled) return
        settled = true
        clearTimeout(timer)

        if (this.version === 3 && this.hideBadge) {
          style.innerHTML = '.grecaptcha-badge { display: none }'
          document.head.appendChild(style)
        } else if (this.version === 2 && this.hideBadge) {
          // display: none DISABLES the spam checking!
          // ref: https://stackoverflow.com/questions/44543157/how-to-hide-google-invisible-recaptcha-badge
          style.innerHTML = '.grecaptcha-badge { visibility: hidden; }'
          document.head.appendChild(style)
        }

        this._grecaptcha = window.grecaptcha.enterprise || window.grecaptcha
        this._grecaptcha.ready(resolve)
      })

      script.addEventListener('error', () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        document.head.removeChild(script)
        this._ready = null
        reject(new Error('ReCaptcha error: Failed to load script'))
      })

      document.head.appendChild(script)
    })

    // 部分调用点（GoogleRecaptcha.vue）不 await init()，这里挂一个空 catch 消费掉 rejection 分支，
    // 避免 unhandledrejection 噪音；await 的调用方仍能正常收到 reject。
    ready.catch(() => {})

    this._ready = ready
    return ready
  }

  setLang(language: string) {
    this.language = language
  }
  getLang() {
    return this.language
  }

  on(event: string, callback: (arg?: any) => void) {
    return this._eventBus.subscribe(event, callback)
  }

  reset(widgetId?: number) {
    if (this.version === 2 || typeof widgetId !== 'undefined') {
      this._grecaptcha.reset(widgetId)
    }
  }

  render(reference: { $el: HTMLElement }, { sitekey, theme }: { sitekey: string; theme: string }) {
    return this._grecaptcha.render(reference.$el || reference, {
      sitekey,
      theme,
    })
  }
}

export type { ReCaptcha }
